import type { EngagementCandidate } from '@clockwise/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { TimesheetEngagementDialog } from '@/components/custom/timesheet-engagement-dialog';
import { customersApi, productsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/** One write the server refused with ENGAGEMENT_REQUIRED, waiting on an answer. */
export interface EngagementRequiredRequest {
  /** The engagements the server said cover the entry. */
  candidates: readonly EngagementCandidate[];
  /** The entry's subject, for the dialog's subtitle. */
  customerId: string | null;
  productId: string | null;
  /** Re-run the refused write with the chosen engagement. */
  onPick: (engagementId: string) => void;
  /** The user declined to choose — the write is abandoned. */
  onCancel: () => void;
}

/** Writes that ask the SAME question share one dialog and one answer. */
const questionKey = (r: EngagementRequiredRequest): string =>
  [r.customerId ?? '', r.productId ?? '', ...r.candidates.map((c) => c.engagementId).sort()].join(
    '|',
  );

/**
 * The safety net behind every timesheet write: when the server refuses one
 * because several engagements cover the entry and none was chosen, ask which
 * and retry. The screens ask up front when an activity pick makes the entry
 * ambiguous, so this fires only when that was bypassed — coverage still
 * loading, an entry saved before the rule existed, a contract amended
 * mid-edit.
 *
 * Refusals are queued, and those asking the same question (same customer,
 * activity and candidates) are merged: re-binding a week-grid row patches up
 * to seven drafts at once, and seven identical dialogs in a row would be
 * absurd. One answer retries every merged write.
 */
export function useEngagementRequiredPrompt() {
  const [queue, setQueue] = useState<EngagementRequiredRequest[][]>([]);

  const ask = (request: EngagementRequiredRequest) => {
    setQueue((prev) => {
      const key = questionKey(request);
      const at = prev.findIndex((group) => group[0] != null && questionKey(group[0]) === key);
      if (at === -1) return [...prev, [request]];
      return prev.map((group, i) => (i === at ? [...group, request] : group));
    });
  };

  const head = queue[0];
  const settle = (fn: (r: EngagementRequiredRequest) => void) => {
    if (!head) return;
    setQueue((prev) => prev.slice(1));
    for (const r of head) fn(r);
  };

  const host = (
    <EngagementRequiredDialog
      request={head?.[0] ?? null}
      onPick={(id) => settle((r) => r.onPick(id))}
      onCancel={() => settle((r) => r.onCancel())}
    />
  );

  return { ask, host };
}

function EngagementRequiredDialog({
  request,
  onPick,
  onCancel,
}: {
  request: EngagementRequiredRequest | null;
  onPick: (engagementId: string) => void;
  onCancel: () => void;
}) {
  // Names for the subtitle, off the shared detail caches — the pickers have
  // usually loaded both already. Fetched only while a question is open.
  const customerId = request?.customerId ?? null;
  const productId = request?.productId ?? null;
  const customer = useQuery({
    queryKey: customerId ? queryKeys.customers.detail(customerId) : ['customers', 'detail', 'none'],
    queryFn: () => customersApi.get(customerId as string),
    enabled: customerId != null,
    staleTime: 60_000,
  });
  const product = useQuery({
    queryKey: productId ? queryKeys.products.detail(productId) : ['products', 'detail', 'none'],
    queryFn: () => productsApi.get(productId as string),
    enabled: productId != null,
    staleTime: 60_000,
  });
  return (
    <TimesheetEngagementDialog
      open={request != null}
      customerName={customerId ? customer.data?.data.name : undefined}
      activityName={productId ? product.data?.data.name : undefined}
      candidates={request?.candidates ?? []}
      others={[]}
      currentEngagementId={null}
      onSelect={onPick}
      onDismiss={onCancel}
    />
  );
}
